import React, { Fragment, useState, useEffect } from "react";
// import Select from "react-select";
import AsyncSelect from "react-select/async";
import { Form, Button, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { Card, CardBody, Col, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import {
  itemsMaster,
  returnPurchase,
  updatePurchaseDetails,
  viewStockDetails,
} from "./StockService";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa6";
import Swal from "sweetalert2";

const UpdateStock = () => {
  const { t } = useTranslation();
  const { stkId } = useParams();
  const { loading, setLoading } = useLoader();
  const { pharmacyPOS } = usePharmacy();
  const history = useNavigate();
  const navigate = useNavigate();

  // Prefill state from existing stock
  const [supplier, setSupplier] = useState("");
  const [items, setItems] = useState([]);
  const [addPayment, setAddPayment] = useState("");
  const [totalPaid, setTotalPaid] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");
  const [errors, setErrors] = useState({});
  const [currency, setCurrency] = useState("");
  const [returnWholePurchase, setReturnWholePurchase] = useState(false);

  useEffect(() => {
    const fetchStock = async () => {
      const body = {
        clinicId: pharmacyPOS?.hospital_id,
        purchaseId: stkId,
      };
      try {
        setLoading(true);
        const response = await viewStockDetails(body);
        if (response.data?.status === "success") {
          const stockData = response.data.data;
          setSupplier(stockData.supplierName || "");
          setNote(stockData.notes || "");
          setDate(
            stockData.purchaseDate ? stockData.purchaseDate.split("T")[0] : ""
          );
          setTotalPaid(stockData.totalPaid || "");
          setCurrency(stockData.selectedCurrency || "");
          // Map stocks to items for editing
          const mappedItems = (stockData.stocks || []).map((item) => ({
            _id: item.itemId, // Use itemId as unique key
            stockId: item._id,
            code: item.code,
            brandName: item.brandName,
            manufacturer: item.manufacturerName,
            package: item.package,
            packageQuantity: item.packing,
            initialPackageQuantity: item.packing,
            stkQuantity: Number(item.quantity), // quantity from API is stkQuantity
            initialStkQuantity: Number(item.quantity),
            itemQuantity: Number(item.totalMedicine), // totalMedicine from API
            initialItemQuantity: Number(item.totalMedicine),
            unitPrice: Number(item.unitPrice), // ensure number
            unitSalePrice: Number(item.unitPricePerMedicine), // ensure number
            batchNo: item.batchNumber,
            manufacturingDate: item.manufacturingDate
              ? item.manufacturingDate.split("T")[0] // ✅ convert to YYYY-MM-DD
              : "",
            expiryDate: item.expiryDate
              ? item.expiryDate.split("T")[0] // ✅ convert to YYYY-MM-DD
              : "",
            discount: item.discount,
          }));
          setItems(mappedItems);
        }
      } catch (error) {
        console.error("Error fetching stock details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStock();
  }, [stkId]);

  // Prefill form on mount (simulate fetch)
  // useEffect(() => {
  // 	// Simulate API call delay
  // 	setTimeout(() => {
  // 		setSupplier(dummyStock.supplier);
  // 		setNote(dummyStock.note);
  // 		setDate(dummyStock.date);
  // 		setTotalPaid(dummyStock.totalPaid);
  // 		setItems(dummyStock.items);
  // 	}, 500);
  // }, []);

  // ✅ Load medicines on search
  const loadMedicines = async (inputValue) => {
    if (!inputValue) return [];

    const body = {
      search: inputValue,
      clinicId: pharmacyPOS?.hospital_id,
    };

    try {
      setLoading(true);
      const response = await itemsMaster(body);
      const medicines = response.data.data || [];

      return medicines.map((m) => ({
        value: m._id,
        label: `${m.brandName} [${m.saltName}]`,
        raw: m, // keep full object for later use
      }));
    } catch (error) {
      console.error("Error fetching medicines:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const handleMedicineSearchChange = (newValue) => {
    setSearchQuery(newValue);
  };

  // ✅ When a medicine is selected
  const handleSelectMedicine = (selectedOption) => {
    if (!selectedOption) return;

    const medicine = selectedOption.raw;
    setItems((prev) => {
      const existing = prev.find((item) => item._id === medicine._id);
      if (existing) {
        return prev;
        // return prev.map((item) =>
        // 	item._id === medicine._id
        // 		? { ...item, quantity: item.quantity + 1 }
        // 		: item
        // );
      } else {
        return [
          ...prev,
          {
            ...medicine,
            stkQuantity: 0,
            itemQuantity: 0,
            unitPrice: 0,
            unitSalePrice: 0,
            batchNo: "",
            manufacturingDate: "",
            expiryDate: "",
            discount: "",
          },
        ];
      }
    });
  };

  const updateQuantity = (_id, value, maxValue) => {
    const val = Math.max(1, Math.min(Number(value), maxValue));
    setItems((prev) =>
      prev.map((item) =>
        item._id === _id
          ? {
            ...item,
            stkQuantity: val,
            itemQuantity: Math.min(
              val * (item.packageQuantity || 1),
              item.initialItemQuantity
            ), // itemQuantity based on package qty, capped at initial
          }
          : item
      )
    );
  };

  const updateItemQuantity = (_id, value, maxValue) => {
    const val = Math.max(1, Math.min(Number(value), maxValue));
    setItems((prev) =>
      prev.map((item) =>
        item._id === _id
          ? { ...item, itemQuantity: val }
          : item
      )
    );
  }

  // Update purchase price
  const updatePurchasePrice = (_id, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === _id
          ? { ...item, unitPrice: Math.max(0, Number(value)) }
          : item
      )
    );
  };

  // Update sale price
  const updateSalePrice = (_id, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === _id
          ? { ...item, unitSalePrice: Math.max(0, Number(value)) }
          : item
      )
    );
  };

  // Update batch number
  const updateBatchNo = (_id, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === _id ? { ...item, batchNo: value } : item
      )
    );
  };

  // Update manufacturing date
  const updateManufacturingDate = (_id, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === _id ? { ...item, manufacturingDate: value } : item
      )
    );
  };

  // Update expiry date
  const updateExpiryDate = (_id, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === _id ? { ...item, expiryDate: value } : item
      )
    );
  };

  // Update batch number
  const updateDiscount = (_id, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === _id ? { ...item, discount: value } : item
      )
    );
  };

  // Remove item
  const removeItem = (_id) => {
    setItems((prev) => prev.filter((item) => item._id !== _id));
  };

  const resetForm = () => {
    setSupplier("");
    setItems([]);
    setTotalPaid("");
    setNote("");
    setDate(new Date().toISOString().split("T")[0]);
    setErrors({});
  };

  // Validation
  const validateForm = () => {
    let newErrors = {};

    if (!supplier.trim()) newErrors.supplier = "Supplier is required";
    // if (items.length === 0) newErrors.items = "At least one item required";
    // if (addPayment === "Yes" && !totalPaid.trim())
    // 	newErrors.totalPaid = "Total paid is required";

    // Item-level validation
    const itemErrors = [];

    items.forEach((item, index) => {
      const errors = {};

      // Batch number check
      if (!item.batchNo || item.batchNo.trim() === "") {
        errors.batchNo = "Required field";
      }

      // Manufacturing date check
      // if (!item.manufacturingDate || item.manufacturingDate.trim() === "") {
      // 	errors.manufacturingDate = "Required field";
      // } else {
      // 	const manufacturing = new Date(item.manufacturingDate);
      // 	const today = new Date();
      // 	today.setHours(0, 0, 0, 0); // remove time part

      // 	if (isNaN(manufacturing.getTime()) || manufacturing > today) {
      // 		errors.manufacturingDate = "Invalid date";
      // 	}
      // }

      // Expiry date check
      if (!item.expiryDate || item.expiryDate.trim() === "") {
        errors.expiryDate = "Required field";
      } else {
        const expiry = new Date(item.expiryDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // remove time part

        if (isNaN(expiry.getTime()) || expiry <= today) {
          errors.expiryDate = "Invalid date";
        }
      }

      itemErrors.push(errors);
    });

    // Check if any item has errors
    const hasItemErrors = itemErrors.some((err) => Object.keys(err).length > 0);

    if (hasItemErrors) {
      newErrors.itemErrors = itemErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update API
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      clinicId: pharmacyPOS?.hospital_id,
      purchaseId: stkId,
      supplierName: supplier || "",
      // NetTotal: netTotal,
      // totalPaid: totalPaid,
      notes: note,
      purchaseDate: date,
      // tax: tax || "0",
      // discount: discount || "0",
      stocks: items.map((item) => ({
        itemId: item._id,
        stockId: item.stockId,
        code: item.code,
        brandName: item.brandName,
        medicineName: item.saltName,
        manufacturerName: item.manufacturer,
        batchNumber: item.batchNo,
        manufacturingDate: item.manufacturingDate,
        expiryDate: item.expiryDate,
        package: item.package,
        packing: item.packageQuantity,
        quantity: item.stkQuantity,
        totalMedicine: item.itemQuantity,
        unitPrice: Number(item.unitPrice).toFixed(2),
        unitPricePerMedicine: Number(item.unitSalePrice).toFixed(2),
        discount: Number(item.discount) || 0,
        // purchaseDate: date,
      })),
    };

    try {
      setLoading(true);
      const response = await updatePurchaseDetails(payload);
      toast.success(response.data.message);
      resetForm();
      gotoList();
    } catch (error) {
      console.error("Error preparing payload:", error);
    } finally {
      setLoading(false);
    }

    console.log("📦 Final Payload:", payload);
    // API call => axios.post("/api/stock/add", payload) ...
  };

  // const handleReturnStock = async (e) => {
  //   e.preventDefault();

  //   const payload = {
  //     clinicId: pharmacyPOS?.hospital_id,
  //     note,
  //     returnDate: new Date().toISOString().split("T")[0],
  //   };

  //   if (returnWholePurchase) {
  //     payload.purchaseId = stkId;
  //   } else {
  //     payload.stockItems = items
  //       .filter(item => item.itemQuantity > 0)
  //       .map(item => ({
  //         stockId: item.stockId,
  //         itemId: item._id,
  //         batchNumber: item.batchNo,
  //         medicineQuantity: item.itemQuantity,
  //       }));
  //   }

  //   try {
  //     setLoading(true);
  //     // Replace with your actual API call
  //     const response = await returnPurchase(payload);
  //     toast.success(response.data.message);
  //     console.log("Return Stock Payload:", payload);
  //     resetForm();
  //     gotoList();
  //   } catch (error) {
  //     console.error("Error returning stock:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Totals calculation (NO TAX, ONLY DISCOUNT)
  const showCalculateItemSubtotal = (item) => {
    const base = item.unitPrice * item.itemQuantity;
    const discount = Number(item.discount) || 0;
    return base - discount >= 0 ? (base - discount).toFixed(2) : 0;
  };

  // Totals calculation (NO TAX, ONLY DISCOUNT)
  const calculateItemSubtotal = (item) => {
    const base = item.unitPrice * item.itemQuantity;
    const discount = Number(item.discount) || 0;
    return base - discount >= 0 ? base - discount : 0;
  };

  const subtotal = items
    ?.reduce((sum, item) => sum + calculateItemSubtotal(item), 0)
    .toFixed(2);

  // Net total is same as subtotal
  const netTotal = subtotal;

  const gotoList = () => {
    history(`${process.env.PUBLIC_URL}/purchase`);
  };

  const handleSettlePayment = (e) => {
    e.preventDefault();

    const actionText = !returnWholePurchase
      ? t("Do you want to return whole purchase?")
      : t("Do you want to return only selected items?");

    const confirmText = !returnWholePurchase
      ? t("Yes, Return whole purchase!")
      : t("Yes, Return only selected items!");

    Swal.fire({
      title: t("Are you sure?"),
      text: actionText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: t("No, Cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        setReturnWholePurchase(!returnWholePurchase);
      }
    });
  };

  const isDark = document.body.classList.contains("dark-only");

  const preventMinus = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={t("Update Purchase")}
        parent={t("Dashboard")}
        title={t("Update Purchase")}
      />
      <Col sm="12">
        <Card>
          <CardBody>
            <div className="d-flex justify-content-end tw-w-full">
              <button className="btn btn-primary" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-2" /> {t("Back")}
              </button>
            </div>
            <Form onSubmit={handleUpdate}>
              <div className="row stock-adjustment">
                {/* Supplier */}
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Supplier")} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={supplier}
                      onChange={(e) => setSupplier(e.target.value)}
                      isInvalid={!!errors.supplier}
                      placeholder={t("Enter supplier name")}
                    // disabled
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.supplier}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>

                {/* Select Items */}
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Select Items")}</Form.Label>
                    <AsyncSelect
                      cacheOptions
                      loadOptions={loadMedicines}
                      defaultOptions={[]} // nothing until search
                      onChange={handleSelectMedicine}
                      placeholder={t("Search & select an item")}
                      onInputChange={handleMedicineSearchChange}
                      noOptionsMessage={() =>
                        searchQuery
                          ? t(
                            "We couldn't find any medications matching your search. Please try a different name."
                          )
                          : t("Start typing to search...")
                      }
                      isClearable={true}
                      isLoading={loading}
                      isDisabled={true}
                      theme={(theme) => ({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primary25: isDark ? "#373737" : "#e6f7ffff",

                        },
                      })}
                    />
                    {errors.items && (
                      <div className="invalid-feedback d-block">
                        {errors.items}
                      </div>
                    )}
                  </Form.Group>
                </div>

                {/* Item Table */}
                <div className="col-md-12">
                  {items?.length > 0 && (
                    <div className="mt-3 mb-3 table-responsive table-wrap stock-adjustment">
                      <Table
                        size="sm"
                        className="align-middle rounded shadow-sm table-bordered"
                      >
                        <thead>
                          <tr>
                            <th>{t("Code")}</th>
                            <th className="text-nowrap">{t("Product Name")}</th>
                            <th>{t("Manufacturer Name")}</th>
                            <th className="text-nowrap">{t("Batch No")}</th>
                            <th>{t("Manufacturing Date")}</th>
                            <th>{t("Expiry Date")}</th>
                            <th>{t("Package")}</th>
                            <th className="text-center">
                              {t("Package Quantity")}
                            </th>
                            <th className="text-center">
                              {t("Item Quantity")}
                            </th>
                            <th className="text-center text-nowrap">
                              {t("Unit Cost")}
                            </th>
                            <th className="text-center text-nowrap">
                              {t("Unit Sale Price")}
                            </th>
                            <th className="text-center">{t("Discount")}</th>
                            <th className="text-center">{t("Subtotal")}</th>
                            <th className="text-center">{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, index) => (
                            <tr key={item._id}>
                              <td className="fw-semibold">{item.code}</td>
                              <td
                                style={{
                                  maxWidth: "100px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-${item._id}`}>
                                      {item.brandName}
                                    </Tooltip>
                                  }
                                >
                                  <span>{item.brandName}</span>
                                </OverlayTrigger>
                              </td>
                              {/* <td className="text-nowrap">{item.manufacturer}</td> */}
                              <td
                                style={{
                                  maxWidth: "100px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-${item._id}`}>
                                      {item.manufacturer}
                                    </Tooltip>
                                  }
                                >
                                  <span>{item.manufacturer}</span>
                                </OverlayTrigger>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={item.batchNo || ""}
                                  className="form-control form-control-sm"
                                  placeholder={t("batch")}
                                  onChange={(e) =>
                                    updateBatchNo(item._id, e.target.value)
                                  }
                                // disabled
                                />
                                {errors.itemErrors?.[index]?.batchNo && (
                                  <small className="text-danger">
                                    {errors.itemErrors[index].batchNo}
                                  </small>
                                )}
                              </td>
                              <td>
                                <input
                                  type="date"
                                  value={item.manufacturingDate || ""}
                                  className="form-control form-control-sm"
                                  max={new Date().toISOString().split("T")[0]} // 🔒 Block future dates
                                  disabled
                                />
                                {errors.itemErrors?.[index]
                                  ?.manufacturingDate && (
                                    <small className="text-danger">
                                      {errors.itemErrors[index].manufacturingDate}
                                    </small>
                                  )}
                              </td>
                              <td>
                                <input
                                  type="date"
                                  value={item.expiryDate || ""}
                                  className="form-control form-control-sm"
                                  min={new Date().toISOString().split("T")[0]} // 🔒 Block past dates
                                  onChange={(e) =>
                                    updateExpiryDate(item._id, e.target.value)
                                  }
                                // disabled
                                />
                                {errors.itemErrors?.[index]?.expiryDate && (
                                  <small className="text-danger">
                                    {errors.itemErrors[index].expiryDate}
                                  </small>
                                )}
                              </td>
                              <td className="text-nowrap">
                                {item.package} ({item.packageQuantity})
                              </td>
                              <td>
                                <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                  <button
                                    type="button"
                                    className="button-minus icon-shape icon-sm disable-count-btn"
                                    // onClick={() =>
                                    //   updateQuantity(
                                    //     item._id,
                                    //     item.stkQuantity - 1,
                                    //     item.initialStkQuantity
                                    //   )
                                    // }
                                    // disabled={item.stkQuantity <= 1}
                                    disabled
                                  >
                                    -
                                  </button>
                                  <input
                                    type="number"
                                    value={item.stkQuantity}
                                    min={1}
                                    max={item.initialStkQuantity}
                                    className="border-0 quantity-field incrementor"
                                    // onChange={e =>
                                    //   updateQuantity(
                                    //     item._id,
                                    //     e.target.value,
                                    //     item.initialStkQuantity
                                    //   )
                                    // }
                                    disabled
                                  />
                                  <button
                                    type="button"
                                    className="button-plus icon-shape icon-sm disable-count-btn"
                                    // onClick={() =>
                                    //   updateQuantity(
                                    //     item._id,
                                    //     item.stkQuantity + 1,
                                    //     item.initialStkQuantity
                                    //   )
                                    // }
                                    // disabled={item.stkQuantity >= item.initialStkQuantity}
                                    disabled
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                              <td>
                                <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                  <button
                                    type="button"
                                    className="button-minus icon-shape icon-sm disable-count-btn"
                                    // onClick={() =>
                                    //   updateItemQuantity(
                                    //     item._id,
                                    //     item.itemQuantity - 1,
                                    //     item.initialItemQuantity
                                    //   )
                                    // }
                                    // disabled={item.itemQuantity <= 1}
                                    disabled
                                  >
                                    -
                                  </button>
                                  <input
                                    type="number"
                                    value={item.itemQuantity}
                                    min={1}
                                    max={item.initialItemQuantity}
                                    className="border-0 quantity-field incrementor"
                                    // onChange={e =>
                                    //   updateItemQuantity(
                                    //     item._id,
                                    //     e.target.value,
                                    //     item.initialItemQuantity
                                    //   )
                                    // }
                                    disabled
                                  />
                                  <button
                                    type="button"
                                    className="button-plus icon-shape icon-sm disable-count-btn"
                                    // onClick={() =>
                                    //   updateItemQuantity(
                                    //     item._id,
                                    //     item.itemQuantity + 1,
                                    //     item.initialItemQuantity
                                    //   )
                                    // }
                                    // disabled={item.itemQuantity >= item.initialItemQuantity}
                                    disabled
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                              <td>
                                <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                  <button
                                    type="button"
                                    className="button-minus icon-shape icon-sm disable-count-btn"
                                    // onClick={() => updatePurchasePrice(item._id, item.unitPrice - 1)}
                                    disabled
                                  >
                                    -
                                  </button>
                                  <input
                                    type="number"
                                    value={item.unitPrice}
                                    className="border-0 quantity-field incrementor"
                                    disabled
                                  />
                                  <button
                                    type="button"
                                    className="button-plus icon-shape icon-sm disable-count-btn"
                                    // onClick={() => updatePurchasePrice(item._id, item.unitPrice + 1)}
                                    disabled
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                              <td>
                                <div className="input-group input-group-sm custom-qty-input table-price-counter-nowrap">
                                  <button
                                    type="button"
                                    className="button-minus icon-shape icon-sm btn-danger"
                                    onClick={() =>
                                      updateSalePrice(
                                        item._id,
                                        item.unitSalePrice - 0.01
                                      )
                                    }
                                  // disabled
                                  >
                                    -
                                  </button>
                                  <input
                                    type="number"
                                    min="0"
                                    onKeyDown={preventMinus}
                                    value={item.unitSalePrice.toFixed(2)}
                                    className="border-0 quantity-field incrementor"
                                    onChange={(e) =>
                                      updateSalePrice(item._id, e.target.value)
                                    }
                                  // disabled
                                  />
                                  <button
                                    type="button"
                                    className="button-plus icon-shape icon-sm btn-primary"
                                    onClick={() =>
                                      updateSalePrice(
                                        item._id,
                                        item.unitSalePrice + 0.01
                                      )
                                    }
                                  // disabled
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={item.discount || ""}
                                  className="text-center form-control form-control-sm discount-field"
                                  placeholder=""
                                  disabled
                                />
                              </td>
                              <td className="text-center fw-bold text-success">
                                ${showCalculateItemSubtotal(item)}
                              </td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="-sm remove-item disable-count-btn"
                                  disabled
                                >
                                  &times;
                                </button>
                              </td>
                            </tr>
                          ))}
                          <tr className="fw-bold bg-light">
                            <td colSpan="12" className="text-end pe-3">
                              {t("Totals")}:
                            </td>
                            <td className="text-center text-primary">
                              ${subtotal}
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>

                {items.length > 0 && (
                  <>
                    <div className="col-md-3">
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Net Total")}</Form.Label>
                        <Form.Control type="number" value={netTotal} disabled />
                      </Form.Group>
                    </div>

                    <div className="col-md-3">
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Currency")}</Form.Label>
                        <Form.Select
                          value={currency}
                          // onChange={(e) => setCurrency(e.target.value)}
                          // isInvalid={!!errors.currency}
                          disabled
                        >
                          {/* <option value="" disabled>{t("Select an Option")}</option> */}
                          <option value="INR">{t("INR")}</option>
                          <option value="USD">{t("USD")}</option>
                          {/* <option value="ZiG">{t("ZiG")}</option> */}
                        </Form.Select>
                        {/* <Form.Control.Feedback type="invalid">
                          {errors.currency}
                        </Form.Control.Feedback> */}
                      </Form.Group>
                    </div>

                    <div className="col-md-3">
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Total Paid")}</Form.Label>
                        <Form.Control
                          type="number"
                          value={totalPaid}
                          // onChange={(e) => setTotalPaid(e.target.value)}
                          isInvalid={!!errors.totalPaid}
                          disabled
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.totalPaid}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    {/* Date */}
                    <div className="col-md-3">
                      <Form.Group className="mb-3">
                        <Form.Label>{t("Purchase Date")}</Form.Label>
                        <Form.Control
                          type="date"
                          value={date}
                          // onChange={(e) => setDate(e.target.value)}
                          disabled
                        />
                      </Form.Group>
                    </div>
                  </>
                )}

                {/* Note */}
                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Note")}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </div>

              {/* Buttons */}
              <div className="gap-2 d-flex justify-content-end">
                <Button variant="primary" type="submit">
                  {t("Update")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.history.back()}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Fragment>
  );
};

export default UpdateStock;
